import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Share2, TrendingUp, Train, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Link } from "react-router-dom";
const TrainConnectionArticle = () => {
  const {
    t
  } = useLanguage();
  const shareArticle = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
  };
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-blue-900">{t('header.title')}</h1>
                <p className="text-blue-600">{t('header.subtitle')}</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <article className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <Badge className="mb-4 bg-green-500">
              <TrendingUp className="h-3 w-3 mr-1" />
              {t('common.expansion')}
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.train.title')}
            </h1>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center text-gray-600">
                <Calendar className="h-4 w-4 mr-2" />
                <span>{t('article.train.date')}</span>
                <span className="mx-2">•</span>
                <span>{t('article.train.readTime')}</span>
              </div>
              <Button onClick={shareArticle} variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                {t('common.share')}
              </Button>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-8">
            <img src="https://images.unsplash.com/photo-1474487548417-781cb71495f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2084&q=80" alt="FS Logistix train connection Antwerp Milan" className="w-full rounded-lg shadow-lg sk-edit-loading h-[384px] object-cover" />
            
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 leading-relaxed">
              {t('article.train.lead')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.train.section1.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.train.section1.content')}
            </p>

            {/* Route Map Card */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Train className="h-5 w-5 mr-2 text-green-600" />
                  {t('article.train.route.title')}
                </CardTitle>
                <CardDescription>{t('article.train.route.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-green-600 mr-3" />
                      <div>
                        <div className="font-semibold">Antwerp Main Hub</div>
                        <div className="text-sm text-gray-600">{t('article.train.route.departure')}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">200,000 m²</div>
                      <div className="text-sm text-gray-600">{t('article.train.route.terminalSize')}</div>
                    </div>
                  </div>
                  
                  <div className="flex justify-center">
                    <div className="w-px h-12 bg-green-300"></div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-blue-600 mr-3" />
                      <div>
                        <div className="font-semibold">Segrate (Milan)</div>
                        <div className="text-sm text-gray-600">{t('article.train.route.arrival')}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">~13h</div>
                      <div className="text-sm text-gray-600">{t('article.train.route.transitTime')}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.train.section2.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.train.section2.content')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-green-900 mb-3">
                {t('article.train.environmental.title')}
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <div className="text-2xl font-bold text-green-800">13,000</div>
                  <div className="text-sm text-green-600">{t('article.train.environmental.trucksRemoved')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-800">-75%</div>
                  <div className="text-sm text-green-600">{t('article.train.environmental.co2Reduction')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-800">5x</div>
                  <div className="text-sm text-green-600">{t('article.train.environmental.weeklyFrequency')}</div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.train.section3.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.train.section3.content')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.train.section4.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.train.section4.content')}
            </p>

            {/* Source Link */}
            <div className="bg-gray-50 p-6 rounded-lg mb-8 border-l-4 border-l-green-500">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t('article.source.title')}
              </h3>
              <p className="text-gray-600 mb-3">{t('article.source.description')}</p>
              <a href="https://breakbulk.news/fs-logistix-takes-over-train-connection-between-antwerp-and-milan/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                <span className="mr-2">🚂</span>
                {t('article.source.readOriginal')}
              </a>
              <p className="text-sm text-gray-500 mt-2">
                {t('article.source.publisher')}: RailFreight.com
              </p>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">
                {t('article.train.partnership.title')}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">FS Logistix:</span>
                  <span>{t('article.train.partnership.fsRole')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Mercitalia Intermodal:</span>
                  <span>{t('article.train.partnership.mercitaliaRole')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Lineas:</span>
                  <span>{t('article.train.partnership.lineasRole')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {t('article.relatedArticles')}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/article/freight-rates">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('industry.freight.title')}</CardTitle>
                    <CardDescription>{t('industry.freight.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/article/carbon-market">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('industry.carbon.title')}</CardTitle>
                    <CardDescription>{t('industry.freight.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>;
};
export default TrainConnectionArticle;